import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { MarketType, OddsOption, SportType } from '@/interface/enum';
import type { IEuroTicket } from '@/libraries/euroTicket';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { isCorrectScoreMarket, isFormatPoint, isShowPointGroup, isShowPointMarket } from '@/libraries/oddsDisplay';
import PriceOptionLib from '@/libraries/priceOption';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export function useEuroTicketDisplay(
    ticket: Ref<IEuroTicket>,
    options: { displayPriceValue?: Ref<number> } = {},
) {
    const leagueName = computed(() => getTranslated(ticket.value.leagueName));
    const homeTeam = computed(() => getTranslated(ticket.value.event.homeTeam?.teamName));
    const awayTeam = computed(() => getTranslated(ticket.value.event.awayTeam?.teamName));
    const marketGroupName = computed(() => getTranslated(ticket.value.odds.marketGroup.name));

    const isCorrectScore = computed(() => isCorrectScoreMarket(ticket.value.marketType));
    const isCorrectScoreAos = computed(() => isCorrectScore.value && ticket.value.priceOption.oddsOption === OddsOption.CS_Others && ticket.value.isLive);

    const displayPriceValue = computed(() => (
        options.displayPriceValue?.value ?? ticket.value.price
    ));
    const displayPrice = computed(() => formatMinDecimalPlace(
        displayPriceValue.value,
        isCorrectScore.value ? 0 : 2,
    ));
    const isOddsNegative = computed(() => displayPriceValue.value < 0);

    const { isEuroSite } = storeToRefs(useFeatureToggleStore());
    const option = computed(() => {
        if (ticket.value.optionName === 'odds_option_home') {
            return homeTeam.value;
        }
        if (ticket.value.optionName === 'odds_option_away') {
            return awayTeam.value;
        }

        if (isCorrectScore.value) {
            const split = ticket.value.optionName.split(';');
            const betOption = ticket.value.option.split(':');

            const homeScore = ticket.value.marketType === MarketType.SH_CorrectScore ? Number(betOption[0]) : Number(betOption[0]) + ticket.value.odds.eventResult.liveHomeScore;
            const awayScore = ticket.value.marketType === MarketType.SH_CorrectScore ? Number(betOption[1]) : Number(betOption[1]) + ticket.value.odds.eventResult.liveAwayScore;

            return i18n.t(split[0], {
                home: homeScore,
                away: awayScore,
            });
        }

        if (isEuroSite.value && ticket.value.marketType === MarketType.HTFT) {
            return PriceOptionLib.getEuroHtFtOption(ticket.value.optionName);
        }

        if (ticket.value.odds
            && PriceOptionLib.betOptionUseTemplate(ticket.value.odds.marketType, ticket.value.odds.marketGroup)) {
            return ticket.value.optionName;
        }

        return PriceOptionLib.getOptionName(
            ticket.value.optionName,
            ticket.value.event,
            ticket.value.marketType,
        );
    });

    const isShowPoint = computed(() => (
        ticket.value.odds
        && isShowPointMarket(ticket.value.odds.marketType)
        && isShowPointGroup(ticket.value.odds.marketGroup.id)
    ));

    const point = computed(() => {
        if (!isShowPoint.value) {
            return '';
        }
        if (isFormatPoint(ticket.value.marketType) && ticket.value.priceOption.oddsOption === OddsOption.Home) {
            return -ticket.value.point;
        }
        return ticket.value.point;
    });

    const isShowLiveScore = computed(() => {
        if (!ticket.value.isLive) {
            return false;
        }

        if (ticket.value.sportType === SportType.Cricket) {
            return false;
        }

        if (ticket.value.marketType === MarketType.SH_CorrectScore) {
            return false;
        }
        return true;
    });

    return {
        leagueName,
        homeTeam,
        awayTeam,
        marketGroupName,
        isCorrectScoreAos,
        displayPrice,
        isOddsNegative,
        option,
        point,
        isShowLiveScore,
    };
}
